import { debounce } from "lodash";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AnyAction } from "redux";
import Table from "../../../core/common/dataTable/index";
import { getAllVpfRequests } from "../../../core/data/redux/vpfSlice";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { all_routes } from "../../router/all_routes";

type AppDispatch = ThunkDispatch<any, any, AnyAction>;

const VpfList = () => {
  const routes = all_routes;
  const dispatch = useDispatch<AppDispatch>();
  const vpfState = useSelector((state: any) => state.vpfState);

  const [dataList, setDataList] = useState<any[]>([]);
  const [query, setQuery] = useState<string>("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  const fetchRecords = (pagination: any, query: string) => {
    dispatch(
      getAllVpfRequests({
        page: pagination.current,
        limit: pagination.pageSize,
        query,
      })
    );
  };

  const handleRefresh = () => {
    fetchRecords(pagination, query);
  };

  const handlePagination = (paginationEvent: any) => {
    const newPagination = {
      current: paginationEvent.current,
      pageSize: paginationEvent.pageSize,
      total: pagination.total, // Keep existing total
    };
    setPagination(newPagination);
    fetchRecords(newPagination, query);
  };

  const debouncedFetchRecords = useCallback(
    debounce((p: any, q: string) => {
      fetchRecords(p, q);
    }, 500),
    []
  );

  const handleSearch = (searchQuery: string) => {
    setQuery(searchQuery);
    const resetPagination = {
      ...pagination,
      current: 1,
    };
    setPagination(resetPagination);

    if (searchQuery === "") {
      debouncedFetchRecords.cancel();
      fetchRecords(resetPagination, "");
    } else {
      debouncedFetchRecords(resetPagination, searchQuery);
    }
  };

  useEffect(() => {
    fetchRecords(pagination, query);
  }, []);

  useEffect(() => {
    if (vpfState.loading === "fulfilled" && vpfState.action === "list") {
      setDataList(vpfState.data || []);
      setPagination((prev) => ({
        ...prev,
        total: vpfState.total || 0,
        pageSize: vpfState.limit || prev.pageSize,
        current: vpfState.page || prev.current,
      }));
    } else if (vpfState.loading === "rejected") {
      console.error("Error fetching VPF requests:", vpfState?.error);
      toast.error(vpfState?.error || "Failed to fetch");
    }
  }, [vpfState.loading, vpfState.action]);

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (text: string, record: any, index: number) => (
        <>
          <div>
            {pagination.current
              ? index +
              pagination.current * pagination.pageSize -
              pagination.pageSize +
              1
              : index + 1}
          </div>
        </>
      ),
      sorter: (a: any, b: any) => a.id.length - b.id.length,
    },
    {
      title: "EmpId",
      dataIndex: "EmpId",
      render: (EmpId: string) => <span>{formatEmpIdForDisplay(EmpId)}</span>,
    },
    {
      title: "Employee Name",
      dataIndex: "EmployeeName",
      render: (text: string) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Approver User ID",
      dataIndex: "ApproverUserID",
      render: (ApproverUserID: string) => (
        <span>{formatEmpIdForDisplay(ApproverUserID)}</span>
      ),
    },

    {
      title: "ReferenceNumber",
      dataIndex: "ReferenceNumber",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "RequestStatus",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "VpfAmount",
      dataIndex: "VpfAmount",
      render: (text: string) => <span>₹{text}</span>,
    },

    {
      title: "Created At",
      dataIndex: "created_at",
      render: (text: string) =>
        text ? new Date(text).toLocaleString() : "N/A",
    },
  ];

  return (
    <>
      <style>
        {`
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Vpf Requests</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Services</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Vpf Requests
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <button
                  className="btn btn-primary d-flex align-items-center"
                  onClick={handleRefresh}
                  disabled={vpfState.loading === "pending"}
                  title="Refresh VPF Requests"
                >
                  <i
                    className={`ti ti-refresh me-2 ${vpfState.loading === "pending" ? "spin" : ""
                      }`}
                  />
                  {vpfState.loading === "pending" ? "Refreshing..." : "Refresh"}
                </button>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-4 mt-3">Vpf Requests</h4>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                columns={columns}
                dataSource={dataList}
                Selection={false}
                paginationConfig={pagination}
                handlePagination={handlePagination}
                loading={
                  vpfState.loading === "pending" && vpfState.action === "list"
                }
                handleSearch={handleSearch}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VpfList;
